import { useState, useCallback } from "react";
import { ImageCropper } from "./ImageCropper";
import { PreviewItem } from "./PreviewItem";
import {
  fetchImageFromUrl,
  getCroppedImage,
  getFilePreviewUrl,
} from "../../../utils/classRoomUtils";
import { s3baseUrl } from "../../../config/config";
import ComponentPopup from "../../GeneralComponents/ComponentPopup";
import logger from "../../../utils/logger";

export const FilePreview = ({ files, setFiles }) => {
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imageRef, setImageRef] = useState(null);

  const handleCropComplete = useCallback((crop, imgRef) => {
    setCompletedCrop(crop);
    setImageRef(imgRef);
  }, []);

  const handleRemoveFile = useCallback(
    (index) => {
      setFiles((prev) => {
        const updated = [...prev];
        if (updated.length > 0) {
          URL.revokeObjectURL(updated[index]?.url);
          updated.splice(index, 1);
        }
        return updated;
      });
    },
    [setFiles]
  );

  const handleCrop = useCallback((path, index) => {
    setSelectedFile(path);
    setSelectedIndex(index);
    setOpenPopup(true);
  }, []);

  const handleCancel = useCallback(() => {
    setSelectedFile(null);
    setCompletedCrop(null);
    setSelectedIndex(null);
    setImageRef(null);
    setOpenPopup(false);
  }, []);

  const applyCrop = useCallback(async () => {
    if (!completedCrop || !imageRef) {
      logger.error("No crop data or image reference");
      return;
    }

    let originalFile = files[selectedIndex];
    let blob;

    if (typeof originalFile === "string") {
      // Handle S3 URL case - fetch the image first
      try {
        blob = await fetchImageFromUrl(`${s3baseUrl}${originalFile}`);
      } catch (error) {
        logger.error("Failed to fetch image from URL:", error);
        return;
      }
    } else {
      // Handle File object case
      blob = originalFile;
    }

    const croppedImageFile = await getCroppedImage(
      imageRef,
      completedCrop,
      originalFile
    );

    if (!croppedImageFile) {
      logger.error("Failed to crop image");
      return;
    }

    setFiles((prev) => {
      const newFiles = [...prev];
      newFiles[selectedIndex] = croppedImageFile;
      return newFiles;
    });

    handleCancel();
  }, [completedCrop, files, imageRef, selectedIndex, setFiles, handleCancel]);

  return (
    <>
      <div className="image-preview-box">
        {files.map((file, index) => (
          <PreviewItem
            key={index}
            previewUrl={getFilePreviewUrl(file, s3baseUrl)}
            onRemove={() => handleRemoveFile(index)}
            onCrop={() => handleCrop(getFilePreviewUrl(file, s3baseUrl), index)}
          />
        ))}
      </div>

      <ComponentPopup
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        onClose={handleCancel}
        title="Crop Image"
        componentToPassDown={
          <div className="image-cropper-box">
            {selectedFile && (
              <ImageCropper
                imageSrc={selectedFile}
                onCropComplete={handleCropComplete}
              />
            )}
            <div className="d-flex justify-content-center gap-4 add--button pb-4">
              <button onClick={applyCrop} disabled={!completedCrop}>
                Apply Crop
              </button>
              <button onClick={handleCancel} className="cancel--button">
                Cancel
              </button>
            </div>
          </div>
        }
      />
    </>
  );
};
